import {
  useCallback, useEffect, useMemo, useRef, useState, type PointerEvent,
} from 'react';
import { Icon } from '../../shell/Icon.tsx';
import { Select } from '../Field.tsx';
import { SegmentedControl } from '../Toolbar.tsx';
import { duration, utcTime } from '../format.ts';
import { STATUS_STATE } from '../state.ts';
import { figure, unread, whyUnread, type Read } from '../states/read.ts';
import type { RobotStatus } from '../../hooks/useRobots.ts';
import { ticks } from '../timeScale.ts';
import {
  AXIS_LABEL, SPANS, spanMs, stampOf, type SpanKey, type TimeAxis,
} from './channels.ts';
import { RATES, type Transport } from './cursor.ts';
import type { EventMark } from './events.ts';
import { presenceRuns, transitions } from './scale.ts';
import type { ChartSeries } from './TimeSeriesCard.tsx';
import { useMeasure } from './useMeasure.ts';
import styles from './charts.module.css';

/* ==========================================================================
   The transport bar — one cursor, one window, for every panel above it.

   `formant-timeline-data-presence-scrubber.png` is the model and the single
   most transferable idea in it is that THE SCRUBBER RENDERS DATA PRESENCE:
   a filled band that breaks where no data exists, so an operator can see the
   holes before scrubbing into one (§13 tell 21 — "a scrubber that lies about
   where data exists"). Ours draws one presence lane per robot, because a
   fleet's coverage is per robot: a stale rover's band stops hours short of
   the right edge while a live one runs to it, and that difference is the
   most useful thing on the strip.

   Above the bands, state transitions sit as 1px ticks at their true
   timestamps, in the tone of the state being entered. Where ticks collide
   they collapse to a boxed count rather than a smear.

   LIVE is a mode, not a position (§5, tell 22). Pressing it while live
   freezes the window where it stands; pressing it in replay returns the
   right edge to wall clock. The pill is filled while live and hollow the
   moment you leave, in one fixed place.
   ========================================================================== */

/** `MM-DD` — printed only where a bare Zulu time would be ambiguous. */
function utcDay(t: number): string {
  const d = new Date(t);
  return `${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/* The tick lane above the presence bands. 11px, because a cluster that
   collapses into a count has to be able to PRINT that count: a 10px numeral
   in a 7px box is the smear the count was there to replace (§13 tell 21). */
const TICK_H = 11;
/** Ticks closer together than this collapse into a count. */
const CLUSTER_PX = 6;
/* The event lane, above the transitions. Its own row rather than the same
   one, because a dispatch and a state change are different claims and a
   reader must be able to tell "we sent this" from "it did this" without
   decoding two shapes on one baseline. */
const EVENT_H = 11;

/**
 * The presence band's height, shared out over the robots on screen.
 *
 * A one-robot band at 8px is a hairline an operator has to hunt for; the same
 * 8px across four robots is four legible lanes. So the track keeps a roughly
 * constant total and the lane takes what it can get, floored at the point
 * where a gap in it would stop being visible.
 */
function laneMetrics(lanes: number): { laneH: number; barH: number } {
  const barH = Math.max(6, Math.min(14, Math.round(38 / Math.max(1, lanes))));
  return { laneH: barH + 2, barH };
}

function toneOf(state: string | null): string {
  if (state === null) return 'neutral';
  return STATUS_STATE[state as RobotStatus]?.tone ?? 'neutral';
}

interface Lane {
  id: string;
  colour: string;
  runs: { start: number; end: number }[];
  marks: { at: number; tone: string }[];
  first: number | null;
  last: number | null;
  count: number;
}

export function TransportBar({
  transport,
  series,
  axis,
  onAxis,
  now,
  read,
  events,
}: {
  transport: Transport;
  series: readonly ChartSeries[];
  axis: TimeAxis;
  onAxis: (a: TimeAxis) => void;
  now: number;
  /**
   * THE READ THE LANES CAME FROM. Required, not optional: every figure in the
   * caption below — how many robots, how much of the track is covered, how
   * many holes there are — is counted from `series`, and `series` is `[]` both
   * when the fleet is quiet and when the endpoint never answered. `0% covered
   * · 0 holes` printed from an unread source is a coverage measurement nobody
   * took, and an operator reads `0 holes` as good news. A ZERO IS A
   * MEASUREMENT AND AN UNREAD SOURCE IS NOT.
   */
  read: Read;
  /* Marks at their true timestamps — commands, on the subject robot. A
     diamond at the instant it was issued and a bar to its resolution
     (§5 timelines; `foxglove-event-editor-timeline-markers.png`). */
  events?: readonly EventMark[] | undefined;
}) {
  const [ref, box] = useMeasure<HTMLDivElement>();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const anchor = useRef<number | null>(null);
  const moved = useRef(false);
  const [drag, setDrag] = useState<{ a: number; b: number } | null>(null);

  const lanes: Lane[] = useMemo(
    () =>
      series.map((s) => {
        const t: number[] = [];
        const st: (string | null)[] = [];
        for (let i = s.readings.length - 1; i >= 0; i -= 1) {
          const r = s.readings[i]!;
          const ts = stampOf(r, axis);
          if (!Number.isFinite(ts)) continue;
          t.push(ts);
          st.push(r.state);
        }
        const deltas: number[] = [];
        for (let i = 1; i < t.length; i += 1) deltas.push(t[i]! - t[i - 1]!);
        deltas.sort((a, b) => a - b);
        const cad = deltas.length > 0 ? deltas[deltas.length >> 1]! : 0;
        const gap = cad > 0 ? cad * 2.5 : Infinity;
        return {
          id: s.id,
          colour: s.colour,
          runs: presenceRuns(t, gap, cad).map((r) => ({ start: r.start, end: r.end })),
          marks: transitions(t, st).map((x) => ({ at: x.at, tone: toneOf(x.to) })),
          first: t[0] ?? null,
          last: t[t.length - 1] ?? null,
          count: t.length,
        };
      }),
    [series, axis],
  );

  /* The track's own domain: everything the store handed us, never less than
     the span the operator picked — so choosing 7 days over a store that holds
     one shows six days of honest emptiness rather than a full band. */
  const oldest = lanes.reduce<number | null>(
    (m, l) => (l.first === null ? m : m === null ? l.first : Math.min(m, l.first)),
    null,
  );
  const extentFrom = Math.min(oldest ?? now - spanMs(transport.span), now - spanMs(transport.span));
  const extentTo = now;
  const extentSpan = Math.max(1, extentTo - extentFrom);

  const w = box.w;
  const { laneH: LANE_H, barH: BAR_H } = laneMetrics(lanes.length);
  const eventH = events !== undefined && events.length > 0 ? EVENT_H : 0;
  const h = eventH + TICK_H + lanes.length * LANE_H;
  const px = useCallback(
    (t: number) => ((t - extentFrom) / extentSpan) * w,
    [extentFrom, extentSpan, w],
  );
  const timeAt = useCallback(
    (clientX: number): number | null => {
      const el = trackRef.current;
      if (!el) return null;
      const r = el.getBoundingClientRect();
      if (r.width <= 0) return null;
      const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      return extentFrom + f * extentSpan;
    },
    [extentFrom, extentSpan],
  );

  const onDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const t = timeAt(e.clientX);
    if (t === null) return;
    anchor.current = t;
    moved.current = false;
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const t = timeAt(e.clientX);
    if (t === null) return;
    if (anchor.current !== null) {
      moved.current = true;
      setDrag({ a: Math.min(anchor.current, t), b: Math.max(anchor.current, t) });
    }
  };
  const onUp = (e: PointerEvent<HTMLDivElement>) => {
    const start = anchor.current;
    anchor.current = null;
    setDrag(null);
    const t = timeAt(e.clientX);
    if (start === null || t === null) return;
    const dist = (Math.abs(t - start) / extentSpan) * w;
    if (moved.current && dist >= 4) {
      transport.zoomTo(Math.min(start, t), Math.max(start, t));
      return;
    }
    /* A click inside the window moves the playhead; a click outside it moves
       the WINDOW, keeping its width — otherwise the only way to reach an hour
       that is off-window would be to brush it exactly. */
    if (t >= transport.from && t <= transport.to) {
      transport.setPlayhead(t);
    } else {
      const half = (transport.to - transport.from) / 2;
      transport.zoomTo(t - half, t + half);
    }
  };

  const cadence =
    lanes.length > 0 && lanes[0]!.count > 1 && lanes[0]!.last !== null && lanes[0]!.first !== null
      ? (lanes[0]!.last! - lanes[0]!.first!) / (lanes[0]!.count - 1)
      : 60_000;

  /* Stable composed ref — an inline one is re-invoked every render, which
     with a measured element is an update loop (see useMeasure). */
  const attach = useCallback(
    (el: HTMLDivElement | null) => {
      ref(el);
      trackRef.current = el;
    },
    [ref],
  );

  const rulerTicks = ticks(extentFrom, extentTo, Math.max(2, Math.round(w / 120)));
  const holes = lanes.reduce((n, l) => n + Math.max(0, l.runs.length - 1), 0);

  /* HOW MUCH OF THE TRACK IS ACTUALLY DATA.

     `holes` counts breaks BETWEEN runs, so a robot that stopped reporting
     seven hours ago and never resumed has zero holes and a track that is two
     thirds empty. Coverage is the honest companion: the share of the drawn
     extent for which at least one robot on screen was reporting. It is the
     number that makes a 7-day window over a one-day store obviously a 7-day
     window over a one-day store (§13 tell 21). */
  const covered = useMemo(() => {
    const iv: { a: number; b: number }[] = [];
    for (const l of lanes) {
      for (const r of l.runs) {
        const a = Math.max(extentFrom, r.start);
        const b = Math.min(extentTo, r.end);
        if (b > a) iv.push({ a, b });
      }
    }
    iv.sort((p, q) => p.a - q.a);
    let total = 0;
    let curA = -Infinity;
    let curB = -Infinity;
    for (const s of iv) {
      if (s.a > curB) {
        if (curB > curA) total += curB - curA;
        curA = s.a;
        curB = s.b;
      } else if (s.b > curB) {
        curB = s.b;
      }
    }
    if (curB > curA) total += curB - curA;
    return total;
  }, [lanes, extentFrom, extentTo]);
  const coveragePct = extentSpan > 0 ? (covered / extentSpan) * 100 : 0;
  const clock = transport.at ?? transport.to;
  /* "Are the numbers on this screen now?" — which needs BOTH halves to be
     true: the window still tracking wall clock, and no playhead pinned in the
     past. A pill that says LIVE over readouts quoting 21:36 is the exact lie
     tell 22 is about. */
  const current = transport.live && transport.playhead === null;
  const crossesDay = utcDay(transport.from) !== utcDay(transport.to);
  /* The clock quotes an instant that may not be today. Say so, once, beside
     the time rather than leaving the operator to infer it from the window. */
  const clockDay = utcDay(clock) === utcDay(now) ? null : utcDay(clock);

  /* §6: space plays, `,`/`.` step, `N` returns to now, esc clears the pin.
     Printed in the bar rather than left to a tooltip.

     NOT `L`. §6 names `L` as "go live" and the app shell has already taken
     it — for the console-wide POLL HOLD, which is a different switch with a
     different meaning. Two window-level listeners on one letter both fire,
     so on this route `L` would resume polling AND unfreeze the window in one
     press, and an operator could not tell which one they had thrown. `N`
     ("now") is unclaimed, and the collision is in the report: the right fix
     is one live/hold concept for the whole console, which is not this lane's
     file to write.

     Ignored while a form control has focus so typing into the compare select
     cannot start a replay, and — for space alone — while a BUTTON has focus,
     because the browser is already going to press that button and firing
     both is how a keyboard operator loses their place. */
  const { setPlaying, playing, step, goLive, setPlayhead } = transport;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (
        tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA'
        || el?.isContentEditable === true
      ) return;
      switch (e.key) {
        case ' ':
          if (tag === 'BUTTON' || tag === 'A') return;
          setPlaying(!playing);
          break;
        case ',': step(-cadence * (e.shiftKey ? 10 : 1)); break;
        case '.': step(cadence * (e.shiftKey ? 10 : 1)); break;
        case 'n': case 'N': goLive(); break;
        case 'Escape': setPlayhead(null); break;
        default: return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setPlaying, playing, step, goLive, setPlayhead, cadence]);

  return (
    <div className={styles.transport}>
      <div className={styles.presenceHead}>
        <span className={styles.eyebrow}>Data presence</span>
        <span
          className={styles.presenceFact}
          title="The whole track: everything the store handed us, never shorter than the span selected."
        >
          {`${duration(extentSpan)} track`}
        </span>
        <span className={styles.presenceFact} title={unread(read) ? whyUnread(read) : undefined}>{`${
          figure(read, lanes.length)
        } ${!unread(read) && lanes.length === 1 ? 'robot' : 'robots'}`}</span>
        <span
          className={styles.presenceFact}
          data-tone={!unread(read) && coveragePct < 90 ? 'warn' : undefined}
          title={
            'Share of the track where at least one robot on screen was reporting. '
            + 'The rest is not "no change" — it is nothing received.'
          }
        >
          {`${figure(read, `${coveragePct.toFixed(0)}%`)} covered`}
        </span>
        <span
          className={styles.presenceFact}
          data-tone={!unread(read) && holes > 0 ? 'warn' : undefined}
          title="Breaks INSIDE a run of coverage. A robot that simply stopped has no hole — it has a short band."
        >
          {`${figure(read, holes)} ${holes === 1 ? 'hole' : 'holes'}`}
        </span>
        {/* Named, because a diamond on a scrub bar is only self-explanatory
            once the strip says what kind of event it is drawing. */}
        {events !== undefined && events.length > 0 && (
          <span
            className={styles.presenceFact}
            title={
              'Commands issued to this robot, at the instant they were issued. '
              + 'The bar runs to the state each one is in now.'
            }
          >
            {`${events.length} ${events.length === 1 ? 'command' : 'commands'}`}
          </span>
        )}
        <span className={styles.headSpacer} />
        <span className={styles.keys}>
          drag a plot to zoom · space play · , . step · N now · esc clear
        </span>
        <span className={styles.headSpacer} />
        <span className={styles.presenceFact}>
          {`window ${duration(transport.to - transport.from)}`}
        </span>
        {/* A bare Zulu time is ambiguous the moment the window crosses a UTC
            midnight — which every 24h and 7d window does. The date is printed
            exactly where it stops being inferable and nowhere else. */}
        <span className={styles.presenceFact}>
          {`${
            crossesDay ? `${utcDay(transport.from)} ` : ''
          }${utcTime(new Date(transport.from).toISOString())} → ${
            crossesDay ? `${utcDay(transport.to)} ` : ''
          }${utcTime(new Date(transport.to).toISOString())}`}
        </span>
      </div>

      <div
        className={styles.trackWrap}
        style={{ height: `${h}px` }}
        ref={attach}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        role="slider"
        tabIndex={0}
        aria-label="Time window over the recorded range. Drag to select a range."
        aria-valuemin={extentFrom}
        aria-valuemax={extentTo}
        aria-valuenow={Math.round(clock)}
        aria-valuetext={utcTime(new Date(clock).toISOString())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') transport.step(-cadence * (e.shiftKey ? 10 : 1));
          else if (e.key === 'ArrowRight') transport.step(cadence * (e.shiftKey ? 10 : 1));
          else if (e.key === 'Home') transport.setPlayhead(transport.from);
          else if (e.key === 'End') transport.setPlayhead(transport.to);
          else if (e.key === 'Escape') transport.setPlayhead(null);
          else if (e.key === 'n' || e.key === 'N') transport.goLive();
          else return;
          e.preventDefault();
        }}
      >
        {w > 0 && (
          <svg width={w} height={h} aria-hidden="true">
            {lanes.map((lane, li) => {
              const y = eventH + TICK_H + li * LANE_H;
              return (
                <g key={lane.id}>
                  <rect
                    x={0} y={y} width={w} height={BAR_H}
                    fill="var(--bg-0)" shapeRendering="crispEdges"
                  />
                  {lane.runs.map((r) => (
                    <rect
                      key={r.start}
                      x={px(r.start)}
                      y={y}
                      width={Math.max(1, px(r.end) - px(r.start))}
                      height={BAR_H}
                      fill={lane.colour}
                      fillOpacity={0.75}
                      shapeRendering="crispEdges"
                    />
                  ))}
                </g>
              );
            })}

            <Marks lanes={lanes} px={px} width={w} top={eventH} />
            {eventH > 0 && (
              <Events marks={events!} px={px} width={w} height={h} />
            )}

            {/* The window is drawn by DIMMING what is outside it rather than
                tinting what is inside: an accent wash over the presence band
                recolours the data it is supposed to be framing. */}
            <rect
              x={0} y={0} width={Math.max(0, px(transport.from))} height={h}
              fill="var(--bg-0)" fillOpacity={0.5} shapeRendering="crispEdges"
            />
            <rect
              x={px(transport.to)} y={0}
              width={Math.max(0, w - px(transport.to))} height={h}
              fill="var(--bg-0)" fillOpacity={0.5} shapeRendering="crispEdges"
            />
            {/* Two accent edges rather than a box: the window's boundaries are
                what an operator drags, and a full outline over a 5px band reads
                as a fill. */}
            <line
              x1={Math.round(px(transport.from)) + 0.5} x2={Math.round(px(transport.from)) + 0.5}
              y1={0} y2={h}
              stroke="var(--accent)" strokeWidth={1} shapeRendering="crispEdges"
            />
            <line
              x1={Math.round(px(transport.to)) - 0.5} x2={Math.round(px(transport.to)) - 0.5}
              y1={0} y2={h}
              stroke="var(--accent)" strokeWidth={1} shapeRendering="crispEdges"
            />
            {drag !== null && (
              <rect
                x={px(drag.a)} y={0}
                width={Math.max(1, px(drag.b) - px(drag.a))} height={h}
                fill="var(--accent-soft)" stroke="var(--accent-line)" strokeWidth={1}
                shapeRendering="crispEdges"
              />
            )}
            {transport.playhead !== null && (
              <line
                x1={px(transport.playhead)} x2={px(transport.playhead)}
                y1={0} y2={h}
                stroke="var(--accent)" strokeWidth={1} shapeRendering="crispEdges"
              />
            )}
          </svg>
        )}
      </div>

      <div className={styles.ruler} data-wide="true">
        {rulerTicks.map((t) =>
          t.f > 0.95 ? null : (
            <span key={t.t} className={styles.rulerTick} style={{ left: `${t.f * 100}%` }}>
              {t.label}
            </span>
          ),
        )}
      </div>

      <div className={styles.controls} role="toolbar" aria-label="Transport">
        <button
          type="button"
          className={styles.livePill}
          data-live={current}
          aria-pressed={current}
          aria-keyshortcuts="N"
          aria-label={current ? 'Live. Press to freeze the window.' : 'Replay. Press to return to now.'}
          title={
            current
              ? 'Live — the window tracks wall clock and the readouts are current. '
                + 'Press to freeze the window where it stands.'
              : transport.live
                ? 'Replay — the cursor is parked in the past. Press to return to now — N'
                : 'Replay — the window is a fixed range. Press to go live — N'
          }
          onClick={() =>
            current ? transport.zoomTo(transport.from, transport.to) : transport.goLive()
          }
        >
          {/* The word is the state and the fill is the state: colour is never
              the only carrier (§ accessibility), and the pill's width is
              pinned so flipping it moves nothing beside it. */}
          {current ? 'LIVE' : 'REPLAY'}
        </button>

        {/* THE SPAN STRIP NAMES THE WINDOW'S DURATION, AND ONLY THAT.

            It reads current whenever the window IS that long — including a
            6h window the operator has dragged back into last night, which is
            still six hours and which this strip used to disown by going
            blank. What genuinely matches nothing is a brush: 1h 20m is not a
            span, and then nothing is filled. Pressing one in replay re-cuts
            the window back from the edge being read rather than jumping to
            now; going live is the pill's job and the pill says so. */}
        <SegmentedControl<SpanKey | ''>
          label="Window"
          value={transport.windowSpan ?? ''}
          onChange={(v) => { if (v !== '') transport.setSpan(v); }}
          items={SPANS.map((s) => ({ value: s.key as SpanKey | '', label: s.key }))}
        />

        <span className={styles.headSpacer} />

        <button
          type="button"
          className={styles.tbtn}
          aria-label="Step back one reading"
          aria-keyshortcuts=","
          title="Step back one reading — ,"
          onClick={() => transport.step(-cadence)}
        >
          <Icon name="chevron-left" size={14} />
        </button>
        <button
          type="button"
          className={styles.tbtn}
          data-on={transport.playing}
          aria-label={transport.playing ? 'Pause replay' : 'Play the window through'}
          aria-keyshortcuts="Space"
          title={`${transport.playing ? 'Pause' : 'Play'} the window through — space`}
          onClick={() => transport.setPlaying(!transport.playing)}
        >
          <Icon name={transport.playing ? 'pause' : 'play'} size={14} />
        </button>
        <button
          type="button"
          className={styles.tbtn}
          aria-label="Step forward one reading"
          aria-keyshortcuts="."
          title="Step forward one reading — ."
          onClick={() => transport.step(cadence)}
        >
          <Icon name="chevron-right" size={14} />
        </button>

        <SegmentedControl<string>
          label="Replay rate"
          value={String(transport.rate)}
          onChange={(v) => transport.setRate(Number(v))}
          items={RATES.map((r) => ({ value: String(r), label: `${r}×` }))}
        />

        <span className={styles.headSpacer} />

        <Select
          size="sm"
          className={styles.axisSel}
          aria-label="Time axis"
          value={axis}
          onChange={(e) => onAxis(e.target.value as TimeAxis)}
        >
          <option value="agent">{AXIS_LABEL.agent}</option>
          <option value="ingest">{AXIS_LABEL.ingest}</option>
        </Select>

        {/* Which instant the clock is quoting. `now` and `end` are different
            claims once the window has been frozen, and a bar that says `now`
            over a stopped clock is the exact lie tell 24 is about. */}
        <span className={styles.clockLabel}>
          {transport.at !== null
            ? transport.hover !== null
              ? 'cursor'
              : 'playhead'
            : transport.live
              ? 'now'
              : 'end'}
        </span>
        <time className={styles.clock} dateTime={new Date(clock).toISOString()}>
          {clockDay !== null && <span className={styles.clockDay}>{`${clockDay} `}</span>}
          {utcTime(new Date(clock).toISOString())}
        </time>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   State-transition marks, clustered when they collide.
   -------------------------------------------------------------------------- */

function Marks({
  lanes,
  px,
  width,
  top,
}: {
  lanes: readonly Lane[];
  px: (t: number) => number;
  width: number;
  /** y of the transitions lane — below the event lane when there is one. */
  top: number;
}) {
  const all = useMemo(() => {
    const out: { x: number; tone: string }[] = [];
    for (const l of lanes) {
      for (const m of l.marks) {
        const x = px(m.at);
        if (x >= 0 && x <= width) out.push({ x, tone: m.tone });
      }
    }
    return out.sort((a, b) => a.x - b.x);
  }, [lanes, px, width]);

  const groups = useMemo(() => {
    const out: { x: number; n: number; tone: string }[] = [];
    for (const m of all) {
      const last = out[out.length - 1];
      if (last !== undefined && m.x - last.x < CLUSTER_PX) {
        last.n += 1;
      } else {
        out.push({ x: m.x, n: 1, tone: m.tone });
      }
    }
    return out;
  }, [all]);

  return (
    <>
      {groups.map((g) =>
        g.n < 3 ? (
          <rect
            key={g.x}
            data-tone={g.tone}
            x={Math.round(g.x)}
            y={top}
            width={1}
            height={TICK_H - 3}
            fill="var(--tone)"
            shapeRendering="crispEdges"
          />
        ) : (
          <g key={g.x}>
            <rect
              x={Math.round(g.x) - 7} y={top} width={15} height={TICK_H - 1}
              fill="var(--bg-2)" stroke="var(--line-3)" strokeWidth={1}
              shapeRendering="crispEdges"
            />
            <text
              className={styles.clusterCount}
              x={Math.round(g.x)}
              y={top + TICK_H - 3}
              textAnchor="middle"
            >
              {g.n}
            </text>
          </g>
        ),
      )}
    </>
  );
}

/* --------------------------------------------------------------------------
   Events — a diamond at the instant, a bar to the resolution.
   -------------------------------------------------------------------------- */

function Events({
  marks,
  px,
  width,
  height,
}: {
  marks: readonly EventMark[];
  px: (t: number) => number;
  width: number;
  /** Full track height: an event's rule crosses every presence lane. */
  height: number;
}) {
  return (
    <>
      {marks.map((m) => {
        const x = px(m.at);
        if (x < -8 || x > width + 8) return null;
        const xe = m.end === null ? x : Math.min(width, px(m.end));
        const cx = Math.round(x) + 0.5;
        return (
          <g key={m.key} data-tone={m.tone}>
            <title>{m.detail}</title>
            {/* The rule crosses the presence bands so the dispatch and the
                coverage under it are read at one x, not two. Quiet, because
                the mark is the diamond — this is only its extension. */}
            <line
              x1={cx} x2={cx} y1={0} y2={height}
              stroke="var(--tone)" strokeOpacity={0.35} strokeWidth={1}
              shapeRendering="crispEdges"
            />
            {xe > x + 1 && (
              <rect
                x={Math.round(x)} y={EVENT_H - 4}
                width={Math.max(1, Math.round(xe - x))} height={2}
                fill="var(--tone)" fillOpacity={0.7} shapeRendering="crispEdges"
              />
            )}
            <path
              d={`M${cx},1 L${cx + 3.5},4.5 L${cx},8 L${cx - 3.5},4.5 Z`}
              fill="var(--tone)"
            />
          </g>
        );
      })}
    </>
  );
}
